import React, { useRef, useEffect, useState } from 'react';
import { X, AlignLeft, AlignCenter, AlignRight, AlignVerticalJustifyCenter, AlignStartVertical, AlignEndVertical, Bold, Italic, Underline } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import ColorPicker from './ColorPicker';
import IconPicker from './IconPicker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Separator } from './ui/separator';

/**
 * Properties Panel - Edit selected element properties
 */
const PropertiesPanel = ({
  selectedElement,
  onUpdateElement,
  onDeleteElement,
  screen,
  globalSettings,
  onUpdateGlobalSettings,
  onUpdateScreen,
  project,
  screens // Add screens prop to get current state
}) => {
  const editorRef = useRef(null);
  const lastElementIdRef = useRef(selectedElement?.id);
  const isTypingRef = useRef(false);
  const [activeStyles, setActiveStyles] = useState({ bold: false, italic: false, underline: false });


  const updateProperty = (key, value) => {
    if (!selectedElement) return;
    onUpdateElement({
      ...selectedElement,
      [key]: value,
    });
  };

  const checkStyles = () => {
    if (document.activeElement === editorRef.current) {
      setActiveStyles({
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline'),
      });
    }
  };

  const handleRichText = (command) => {
    document.execCommand(command, false, null);
    if (editorRef.current) {
      updateProperty('content', editorRef.current.innerHTML);
      checkStyles();
    }
  };

  useEffect(() => {
    const handleSelectionChange = () => checkStyles();
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, []);

  useEffect(() => {
    if (!selectedElement) return;

    // If element changed, always update
    if (selectedElement.id !== lastElementIdRef.current) {
      lastElementIdRef.current = selectedElement.id;
      isTypingRef.current = false;
      if (editorRef.current) {
        editorRef.current.innerHTML = selectedElement.content || '';
      }
      return;
    }

    // If typing, skip update but reset flag
    if (isTypingRef.current) {
      isTypingRef.current = false;
      return;
    }

    // If same element and not typing, update if content is different
    if (editorRef.current && selectedElement.content !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = selectedElement.content || '';
    }
  }, [selectedElement?.content, selectedElement?.id]);

  if (!selectedElement) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Screen Settings</CardTitle>
            <CardDescription>
              Settings for the current screen
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Screen Name</Label>
              <Input
                value={screen?.name || ''}
                onChange={(e) => onUpdateScreen?.({ ...screen, name: e.target.value })}
                placeholder="Screen name"
              />
            </div>

            <Separator />

            <div className="space-y-2">
              <Label>Background Color</Label>
              <ColorPicker
                color={screen?.backgroundColor || globalSettings?.backgroundColor || '#ffffff'}
                onChange={(color) => onUpdateScreen?.({ ...screen, backgroundColor: color })}
              />
              <p className="text-xs text-muted-foreground">
                Override the global background color for this screen.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Global Settings</CardTitle>
            <CardDescription>
              Settings applied to the entire project
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Font Family</Label>
              <Select
                value={globalSettings?.fontFamily || 'Inter'}
                onValueChange={(value) => onUpdateGlobalSettings({ ...globalSettings, fontFamily: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Font" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Inter">Inter</SelectItem>
                  <SelectItem value="Roboto">Roboto</SelectItem>
                  <SelectItem value="Open Sans">Open Sans</SelectItem>
                  <SelectItem value="Lato">Lato</SelectItem>
                  <SelectItem value="Montserrat">Montserrat</SelectItem>
                  <SelectItem value="Poppins">Poppins</SelectItem>
                  <SelectItem value="Oswald">Oswald</SelectItem>
                  <SelectItem value="Raleway">Raleway</SelectItem>
                  <SelectItem value="Merriweather">Merriweather</SelectItem>
                  <SelectItem value="Playfair Display">Playfair Display</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                This font will be applied to all text elements unless overridden.
              </p>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label>Background Color (Default)</Label>
              <ColorPicker
                color={globalSettings?.backgroundColor || '#ffffff'}
                onChange={(color) => onUpdateGlobalSettings({ ...globalSettings, backgroundColor: color })}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const updateStyle = (key, value) => {
    const newStyles = {
      ...selectedElement.styles,
      [key]: value,
    };
    onUpdateElement({
      ...selectedElement,
      styles: newStyles,
    });
  };



  const handleAlign = (alignment) => {
    if (!screen) return;

    let newX = selectedElement.x;
    let newY = selectedElement.y;
    const screenWidth = screen.width || 375;
    const screenHeight = screen.height || 812;

    switch (alignment) {
      case 'left':
        newX = 0;
        break;
      case 'center-h':
        newX = (screenWidth - selectedElement.width) / 2;
        break;
      case 'right':
        newX = screenWidth - selectedElement.width;
        break;
      case 'top':
        newY = 0;
        break;
      case 'center-v':
        newY = (screenHeight - selectedElement.height) / 2;
        break;
      case 'bottom':
        newY = screenHeight - selectedElement.height;
        break;
      default:
        break;
    }

    onUpdateElement({
      ...selectedElement,
      x: newX,
      y: newY,
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{selectedElement.name || selectedElement.type}</CardTitle>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-destructive hover:text-destructive"
              onClick={() => onDeleteElement(selectedElement.id)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Content */}
          {(selectedElement.type === 'text' || selectedElement.type === 'button') && (
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label>Content</Label>
                <div className="flex gap-1">
                  <Button
                    variant={activeStyles.bold ? 'default' : 'ghost'}
                    size="icon"
                    className="h-6 w-6"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleRichText('bold')}
                    title="Bold"
                  >
                    <Bold className="h-3 w-3" />
                  </Button>
                  <Button
                    variant={activeStyles.italic ? 'default' : 'ghost'}
                    size="icon"
                    className="h-6 w-6"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleRichText('italic')}
                    title="Italic"
                  >
                    <Italic className="h-3 w-3" />
                  </Button>
                  <Button
                    variant={activeStyles.underline ? 'default' : 'ghost'}
                    size="icon"
                    className="h-6 w-6"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleRichText('underline')}
                    title="Underline"
                  >
                    <Underline className="h-3 w-3" />
                  </Button>
                </div>
              </div>
              <div
                key={selectedElement.id}
                ref={(el) => {
                  editorRef.current = el;
                  if (el && selectedElement && !el.innerHTML) {
                    el.innerHTML = selectedElement.content || '';
                  }
                }}
                contentEditable
                className="block min-h-[40px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 cursor-text"
                onInput={(e) => {
                  isTypingRef.current = true;
                  updateProperty('content', e.currentTarget.innerHTML);
                }}
                suppressContentEditableWarning={true}
                style={{ whiteSpace: 'pre-wrap' }}
              />

              {/* Dynamic Data Insertion Helper */}
              {(() => {
                // Collect all input variables from all screens
                const allInputs = [];
                if (screens && Array.isArray(screens)) {
                  screens.forEach((scr, screenIndex) => {
                    (scr.elements || []).forEach(element => {
                      if (element.type === 'input' && element.variableName) {
                        allInputs.push({
                          variableName: element.variableName,
                          label: element.label || element.variableName,
                          screenName: scr.name || `Screen ${screenIndex + 1}`
                        });
                      }
                    });
                  });
                }

                if (allInputs.length > 0) {
                  return (
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Insert Dynamic Data</Label>
                      <div className="flex flex-wrap gap-1">
                        {allInputs.map((input, idx) => (
                          <Button
                            key={idx}
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => {
                              const variable = `{{${input.variableName}}}`;
                              if (editorRef.current) {
                                // Insert at cursor or append
                                const selection = window.getSelection();
                                if (selection && selection.rangeCount > 0 && editorRef.current.contains(selection.anchorNode)) {
                                  const range = selection.getRangeAt(0);
                                  range.deleteContents();
                                  const textNode = document.createTextNode(variable);
                                  range.insertNode(textNode);
                                  range.setStartAfter(textNode);
                                  range.setEndAfter(textNode);
                                  selection.removeAllRanges();
                                  selection.addRange(range);
                                } else {
                                  editorRef.current.innerHTML += variable;
                                }
                                updateProperty('content', editorRef.current.innerHTML);
                              }
                            }}
                            title={`From: ${input.screenName}`}
                          >
                            {input.label}
                          </Button>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Click to insert data from inputs. Use <code className="px-1 py-0.5 bg-muted rounded text-xs">{'{{variableName}}'}</code> syntax.
                      </p>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          )}

          <Separator />

          {/* Position & Size */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold">Position & Size</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs">X</Label>
                <Input
                  type="number"
                  value={Math.round(selectedElement.x || 0)}
                  onChange={(e) => updateProperty('x', parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Y</Label>
                <Input
                  type="number"
                  value={Math.round(selectedElement.y || 0)}
                  onChange={(e) => updateProperty('y', parseFloat(e.target.value) || 0)}
                />
              </div>
            </div>

            {/* Alignment Controls */}
            <div className="grid grid-cols-6 gap-1">
              <Button variant="outline" size="icon" className="h-8 w-full" onClick={() => handleAlign('left')} title="Align Left">
                <AlignLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" className="h-8 w-full" onClick={() => handleAlign('center-h')} title="Align Center Horizontal">
                <AlignCenter className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" className="h-8 w-full" onClick={() => handleAlign('right')} title="Align Right">
                <AlignRight className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" className="h-8 w-full" onClick={() => handleAlign('top')} title="Align Top">
                <AlignStartVertical className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" className="h-8 w-full" onClick={() => handleAlign('center-v')} title="Align Center Vertical">
                <AlignVerticalJustifyCenter className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" className="h-8 w-full" onClick={() => handleAlign('bottom')} title="Align Bottom">
                <AlignEndVertical className="h-4 w-4" />
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs">Width</Label>
                <Input
                  type="number"
                  value={Math.round(selectedElement.width || 0)}
                  onChange={(e) => updateProperty('width', parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Height</Label>
                <Input
                  type="number"
                  value={Math.round(selectedElement.height || 0)}
                  onChange={(e) => updateProperty('height', parseFloat(e.target.value) || 0)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Rotation (degrees)</Label>
              <Input
                type="number"
                value={selectedElement.rotation || 0}
                onChange={(e) => updateProperty('rotation', parseFloat(e.target.value) || 0)}
                min="0"
                max="360"
                step="1"
              />
            </div>
          </div>

          {/* Typography */}
          {(selectedElement.type === 'text' || selectedElement.type === 'button') && (
            <>
              <Separator />
              <div className="space-y-4">
                <h4 className="text-sm font-semibold">Typography</h4>
                <div className="space-y-2">
                  <Label className="text-xs">Font Family</Label>
                  <Select
                    value={selectedElement.styles?.fontFamily || globalSettings?.fontFamily || 'Inter'}
                    onValueChange={(value) => updateStyle('fontFamily', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Inter">Inter</SelectItem>
                      <SelectItem value="Roboto">Roboto</SelectItem>
                      <SelectItem value="Open Sans">Open Sans</SelectItem>
                      <SelectItem value="Lato">Lato</SelectItem>
                      <SelectItem value="Montserrat">Montserrat</SelectItem>
                      <SelectItem value="Poppins">Poppins</SelectItem>
                      <SelectItem value="Oswald">Oswald</SelectItem>
                      <SelectItem value="Raleway">Raleway</SelectItem>
                      <SelectItem value="Merriweather">Merriweather</SelectItem>
                      <SelectItem value="Playfair Display">Playfair Display</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <Label className="text-xs">Font Size</Label>
                      <span className="text-xs text-muted-foreground">{parseInt(selectedElement.styles?.fontSize) || 16}px</span>
                    </div>
                    <Slider
                      value={[parseInt(selectedElement.styles?.fontSize) || 16]}
                      onValueChange={(value) => updateStyle('fontSize', `${value[0]}px`)}
                      min={8}
                      max={72}
                      step={1}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Font Weight</Label>
                    <Select
                      value={selectedElement.styles?.fontWeight || '400'}
                      onValueChange={(value) => updateStyle('fontWeight', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="300">Light</SelectItem>
                        <SelectItem value="400">Regular</SelectItem>
                        <SelectItem value="500">Medium</SelectItem>
                        <SelectItem value="600">Semi Bold</SelectItem>
                        <SelectItem value="700">Bold</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Text Align</Label>
                  <Select
                    value={selectedElement.styles?.textAlign || 'left'}
                    onValueChange={(value) => updateStyle('textAlign', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="left">Left</SelectItem>
                      <SelectItem value="center">Center</SelectItem>
                      <SelectItem value="right">Right</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}

          {/* Colors */}
          <Separator />
          <div className="space-y-4">
            <h4 className="text-sm font-semibold">Colors</h4>
            {(selectedElement.type === 'text' || selectedElement.type === 'button' || selectedElement.type === 'multiple-choice') && (
              <div className="space-y-2">
                <Label className="text-xs">Text Color</Label>
                <ColorPicker
                  color={selectedElement.styles?.color || '#1f2937'}
                  onChange={(color) => updateStyle('color', color)}
                />
              </div>
            )}
            {(selectedElement.type === 'button' || selectedElement.type === 'image' || selectedElement.type === 'multiple-choice' || selectedElement.type === 'shape') && (
              <div className="space-y-2">
                <Label className="text-xs">Background Color</Label>
                <ColorPicker
                  color={selectedElement.styles?.backgroundColor || (selectedElement.type === 'multiple-choice' ? '#ffffff' : '#3b82f6')}
                  onChange={(color) => updateStyle('backgroundColor', color)}
                />
              </div>
            )}
            {selectedElement.type === 'multiple-choice' && (
              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label className="text-xs">Active Color</Label>
                </div>
                <ColorPicker
                  color={selectedElement.styles?.activeColor || '#3b82f6'}
                  onChange={(color) => updateStyle('activeColor', color)}
                />
              </div>
            )}
          </div>

          {/* Border & Radius */}
          {(selectedElement.type === 'button' || selectedElement.type === 'image' || selectedElement.type === 'multiple-choice' || selectedElement.type === 'shape') && (
            <>
              <Separator />
              <div className="space-y-4">
                <h4 className="text-sm font-semibold">Border & Radius</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs">Border Width</Label>
                    <div className="flex items-center gap-2">
                      <Slider
                        value={[parseInt(selectedElement.styles?.borderWidth) || 0]}
                        onValueChange={(value) => updateStyle('borderWidth', `${value[0]}px`)}
                        min={0}
                        max={20}
                        step={1}
                        className="flex-1"
                      />
                      <span className="text-xs w-8 text-right">{parseInt(selectedElement.styles?.borderWidth) || 0}px</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Border Radius</Label>
                    <div className="flex items-center gap-2">
                      <Slider
                        value={[parseInt(selectedElement.styles?.borderRadius) || 0]}
                        onValueChange={(value) => updateStyle('borderRadius', `${value[0]}px`)}
                        min={0}
                        max={100}
                        step={1}
                        className="flex-1"
                      />
                      <span className="text-xs w-8 text-right">{parseInt(selectedElement.styles?.borderRadius) || 0}px</span>
                    </div>
                  </div>
                </div>
                {parseInt(selectedElement.styles?.borderWidth) > 0 && (
                  <div className="space-y-2">
                    <Label className="text-xs">Border Color</Label>
                    <ColorPicker
                      color={selectedElement.styles?.borderColor || '#d1d5db'}
                      onChange={(color) => updateStyle('borderColor', color)}
                    />
                  </div>
                )}
              </div>
            </>
          )}

          {/* Effects (Shadow & Opacity) - for buttons, images, and shapes */}
          {(selectedElement.type === 'button' || selectedElement.type === 'image' || selectedElement.type === 'shape') && (
            <>
              <Separator />
              <div className="space-y-4">
                <h4 className="text-sm font-semibold">Effects</h4>

                {/* Opacity - only for shapes and images */}
                {(selectedElement.type === 'shape' || selectedElement.type === 'image') && (
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <Label className="text-xs">Opacity</Label>
                      <span className="text-xs text-muted-foreground">{Math.round((selectedElement.styles?.opacity || 1) * 100)}%</span>
                    </div>
                    <Slider
                      value={[(selectedElement.styles?.opacity || 1) * 100]}
                      onValueChange={(value) => updateStyle('opacity', value[0] / 100)}
                      min={0}
                      max={100}
                      step={1}
                    />
                  </div>
                )}

                {/* Shadow */}
                <div className="space-y-2">
                  <Label className="text-xs">Shadow</Label>
                  <Select
                    value={(() => {
                      const shadow = selectedElement.styles?.boxShadow || 'none';
                      // Check if current value matches a preset
                      const presets = [
                        'none',
                        '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                        '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                        '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                        '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
                        '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                        'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)'
                      ];
                      return presets.includes(shadow) ? shadow : 'custom';
                    })()}
                    onValueChange={(value) => updateStyle('boxShadow', value === 'none' ? '' : value === 'custom' ? selectedElement.styles?.boxShadow : value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="0 1px 2px 0 rgba(0, 0, 0, 0.05)">Small</SelectItem>
                      <SelectItem value="0 4px 6px -1px rgba(0, 0, 0, 0.1)">Medium</SelectItem>
                      <SelectItem value="0 10px 15px -3px rgba(0, 0, 0, 0.1)">Large</SelectItem>
                      <SelectItem value="0 20px 25px -5px rgba(0, 0, 0, 0.1)">Extra Large</SelectItem>
                      <SelectItem value="0 25px 50px -12px rgba(0, 0, 0, 0.25)">2XL</SelectItem>
                      <SelectItem value="inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)">Inner</SelectItem>
                      {selectedElement.styles?.boxShadow && !['none', '', '0 1px 2px 0 rgba(0, 0, 0, 0.05)', '0 4px 6px -1px rgba(0, 0, 0, 0.1)', '0 10px 15px -3px rgba(0, 0, 0, 0.1)', '0 20px 25px -5px rgba(0, 0, 0, 0.1)', '0 25px 50px -12px rgba(0, 0, 0, 0.25)', 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)'].includes(selectedElement.styles.boxShadow) && (
                        <SelectItem value="custom">Custom</SelectItem>
                      )}
                    </SelectContent>
                  </Select>

                  {/* Shadow fine-tuning sliders - only show if shadow is not 'none' */}
                  {selectedElement.styles?.boxShadow && selectedElement.styles.boxShadow !== '' && (
                    <div className="space-y-3 pt-2 pl-2 border-l-2 border-muted">
                      <div className="space-y-1">
                        <div className="flex justify-between">
                          <Label className="text-xs text-muted-foreground">Blur</Label>
                          <span className="text-xs text-muted-foreground">
                            {(() => {
                              const shadow = selectedElement.styles.boxShadow;
                              const pxValues = shadow.match(/\d+px/g);
                              const blur = pxValues && pxValues[2] ? parseInt(pxValues[2]) : 0;
                              return `${blur}px`;
                            })()}
                          </span>
                        </div>
                        <Slider
                          value={[(() => {
                            const shadow = selectedElement.styles.boxShadow;
                            const pxValues = shadow.match(/\d+px/g);
                            const blur = pxValues && pxValues[2] ? parseInt(pxValues[2]) : 0;
                            return blur;
                          })()]}
                          onValueChange={(value) => {
                            const current = selectedElement.styles.boxShadow;
                            let count = 0;
                            const newShadow = current.replace(/\d+px/g, (match) => {
                              count++;
                              return count === 3 ? `${value[0]}px` : match;
                            });
                            updateStyle('boxShadow', newShadow);
                          }}
                          min={0}
                          max={50}
                          step={1}
                        />
                      </div>
                      <div className="space-y-1">
                        <div className="flex justify-between">
                          <Label className="text-xs text-muted-foreground">Opacity</Label>
                          <span className="text-xs text-muted-foreground">
                            {(() => {
                              const match = selectedElement.styles.boxShadow.match(/rgba\(0,\s*0,\s*0,\s*([\d.]+)\)/);
                              return match ? `${Math.round(parseFloat(match[1]) * 100)}%` : '0%';
                            })()}
                          </span>
                        </div>
                        <Slider
                          value={[(() => {
                            const match = selectedElement.styles.boxShadow.match(/rgba\(0,\s*0,\s*0,\s*([\d.]+)\)/);
                            return match ? parseFloat(match[1]) * 100 : 0;
                          })()]}
                          onValueChange={(value) => {
                            const current = selectedElement.styles.boxShadow;
                            const newShadow = current.replace(/rgba\(0,\s*0,\s*0,\s*[\d.]+\)/, `rgba(0, 0, 0, ${(value[0] / 100).toFixed(2)})`);
                            updateStyle('boxShadow', newShadow);
                          }}
                          min={0}
                          max={100}
                          step={1}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}



          {/* Image URL */}
          {selectedElement.type === 'image' && (
            <>
              <Separator />
              <div className="space-y-2">
                <Label className="text-xs">Image URL</Label>
                <Input
                  type="text"
                  value={selectedElement.imageUrl || ''}
                  onChange={(e) => updateProperty('imageUrl', e.target.value)}
                  placeholder="https://example.com/image.jpg"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Or Upload Image</Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        updateProperty('imageUrl', reader.result);
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                  className="cursor-pointer"
                />
                <p className="text-xs text-muted-foreground">Upload a local image file</p>
              </div>
            </>
          )}

          {/* Action */}
          {selectedElement.type === 'button' && (
            <>
              <Separator />
              <div className="space-y-2">
                <Label className="text-xs">Action</Label>
                <Select
                  value={selectedElement.action || 'none'}
                  onValueChange={(value) => updateProperty('action', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Action</SelectItem>
                    <SelectItem value="next">Go to Next Screen</SelectItem>
                    <SelectItem value="back">Go to Previous Screen</SelectItem>
                    <SelectItem value="dismiss">Dismiss Onboarding</SelectItem>
                    <SelectItem value="skip">Skip</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {(selectedElement.action === 'next' || selectedElement.action === 'back') && (
                <div className="space-y-2">
                  <Label className="text-xs">Transition Animation</Label>
                  <Select
                    value={selectedElement.transition || 'slide'}
                    onValueChange={(value) => updateProperty('transition', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="slide">Slide</SelectItem>
                      <SelectItem value="fade">Fade</SelectItem>
                      <SelectItem value="push">Push</SelectItem>
                      <SelectItem value="modal">Modal</SelectItem>
                      <SelectItem value="flip">Flip</SelectItem>
                      <SelectItem value="zoom">Zoom</SelectItem>
                      <SelectItem value="none">None</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Animation when navigating to the next screen
                  </p>
                </div>
              )}
            </>
          )}

          {/* Multiple Choice Options */}
          {selectedElement.type === 'multiple-choice' && (
            <>
              <Separator />
              <div className="space-y-2">
                <Label className="text-xs">Selection Type</Label>
                <Select
                  value={selectedElement.selectionType || 'single'}
                  onValueChange={(value) => updateProperty('selectionType', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Single Select</SelectItem>
                    <SelectItem value="multiple">Multi Select</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Options</Label>
                {((selectedElement.options && Array.isArray(selectedElement.options)) ? selectedElement.options : []).map((option, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Input
                      type="text"
                      value={option}
                      onChange={(e) => {
                        const newOptions = [...(selectedElement.options || [])];
                        newOptions[idx] = e.target.value;
                        updateProperty('options', newOptions);
                      }}
                      placeholder={`Option ${idx + 1}`}
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-10 w-10"
                      onClick={() => {
                        const newOptions = selectedElement.options.filter((_, i) => i !== idx);
                        updateProperty('options', newOptions);
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    const newOptions = [...(selectedElement.options || []), `Option ${(selectedElement.options || []).length + 1}`];
                    updateProperty('options', newOptions);
                  }}
                >
                  + Add Option
                </Button>
              </div>
            </>
          )}

          {/* Input Properties */}
          {selectedElement.type === 'input' && (
            <>
              <Separator />
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs">Label</Label>
                  <Input
                    type="text"
                    value={selectedElement.label || ''}
                    onChange={(e) => updateProperty('label', e.target.value)}
                    placeholder="Label text"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Placeholder</Label>
                  <Input
                    type="text"
                    value={selectedElement.placeholder || ''}
                    onChange={(e) => updateProperty('placeholder', e.target.value)}
                    placeholder="Placeholder text"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Variable Name (for data collection)</Label>
                  <Input
                    type="text"
                    value={selectedElement.variableName || ''}
                    onChange={(e) => updateProperty('variableName', e.target.value)}
                    placeholder="e.g. email, full_name"
                    className="font-mono text-xs"
                  />
                </div>

                {/* Supabase Integration - Commented out for now
                <Separator />
                <h4 className="text-sm font-semibold">Supabase Integration (Optional)</h4>
                <p className="text-xs text-muted-foreground">Configure in Settings → Supabase Integration first</p>
                <div className="space-y-2">
                  <Label className="text-xs">Table Name</Label>
                  <select
                    value={selectedElement.supabaseTable || ''}
                    onChange={(e) => updateProperty('supabaseTable', e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="">Select a table...</option>
                    {(project?.supabase_settings?.tables || []).map(table => (
                      <option key={table.name} value={table.name}>{table.name}</option>
                    ))}
                  </select>
                </div>
                {selectedElement.supabaseTable && (
                  <div className="space-y-2">
                    <Label className="text-xs">Column Name</Label>
                    <select
                      value={selectedElement.supabaseColumn || ''}
                      onChange={(e) => updateProperty('supabaseColumn', e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="">Select a column...</option>
                      {(project?.supabase_settings?.tables || [])
                        .find(t => t.name === selectedElement.supabaseTable)
                        ?.columns?.map(col => (
                          <option key={col} value={col}>{col}</option>
                        ))}
                    </select>
                  </div>
                )}
                */}

                <Separator />
                <h4 className="text-sm font-semibold">Input Styling</h4>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs">Text Color</Label>
                    <ColorPicker
                      color={selectedElement.styles?.color || '#1f2937'}
                      onChange={(color) => updateStyle('color', color)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Background</Label>
                    <ColorPicker
                      color={selectedElement.styles?.backgroundColor || '#ffffff'}
                      onChange={(color) => updateStyle('backgroundColor', color)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs">Border Color</Label>
                  <ColorPicker
                    color={selectedElement.styles?.borderColor || '#e5e7eb'}
                    onChange={(color) => updateStyle('borderColor', color)}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs">Border Width</Label>
                    <div className="flex items-center gap-2">
                      <Slider
                        value={[parseInt(selectedElement.styles?.borderWidth) || 1]}
                        onValueChange={(value) => updateStyle('borderWidth', `${value[0]}px`)}
                        min={0}
                        max={10}
                        step={1}
                        className="flex-1"
                      />
                      <span className="text-xs w-8 text-right">{parseInt(selectedElement.styles?.borderWidth) || 1}px</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Border Radius</Label>
                    <div className="flex items-center gap-2">
                      <Slider
                        value={[parseInt(selectedElement.styles?.borderRadius) || 8]}
                        onValueChange={(value) => updateStyle('borderRadius', `${value[0]}px`)}
                        min={0}
                        max={50}
                        step={1}
                        className="flex-1"
                      />
                      <span className="text-xs w-8 text-right">{parseInt(selectedElement.styles?.borderRadius) || 8}px</span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Icon Properties */}
          {selectedElement.type === 'icon' && (
            <>
              <Separator />
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs">Icon</Label>
                  <IconPicker
                    value={selectedElement.iconName || 'star'}
                    onChange={(value) => updateProperty('iconName', value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs">Icon Color</Label>
                  <ColorPicker
                    color={selectedElement.styles?.color || '#3b82f6'}
                    onChange={(color) => updateStyle('color', color)}
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label className="text-xs">Icon Size</Label>
                    <span className="text-xs text-muted-foreground">{selectedElement.iconSize || 24}px</span>
                  </div>
                  <Slider
                    value={[selectedElement.iconSize || 24]}
                    onValueChange={(value) => updateProperty('iconSize', value[0])}
                    min={12}
                    max={100}
                    step={1}
                  />
                </div>

                {/* Icon Action */}
                <div className="space-y-2">
                  <Label className="text-xs">Action</Label>
                  <Select
                    value={selectedElement.action || 'none'}
                    onValueChange={(value) => updateProperty('action', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Action</SelectItem>
                      <SelectItem value="next">Go to Next Screen</SelectItem>
                      <SelectItem value="back">Go to Previous Screen</SelectItem>
                      <SelectItem value="dismiss">Dismiss Onboarding</SelectItem>
                      <SelectItem value="skip">Skip</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}

          {/* Dots Indicator Properties */}
          {selectedElement.type === 'dots-indicator' && (
            <>
              <Separator />
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs">Total Screens</Label>
                    <Input
                      type="number"
                      value={selectedElement.totalScreens || 3}
                      onChange={(e) => updateProperty('totalScreens', parseInt(e.target.value) || 3)}
                      min="1"
                      max="20"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Current Screen</Label>
                    <Input
                      type="number"
                      value={selectedElement.currentScreen || 0}
                      onChange={(e) => updateProperty('currentScreen', parseInt(e.target.value) || 0)}
                      min="0"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label className="text-xs">Dot Size</Label>
                    <span className="text-xs text-muted-foreground">{parseInt(selectedElement.styles?.dotSize) || 8}px</span>
                  </div>
                  <Slider
                    value={[parseInt(selectedElement.styles?.dotSize) || 8]}
                    onValueChange={(value) => updateStyle('dotSize', `${value[0]}px`)}
                    min={4}
                    max={20}
                    step={1}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs">Active Color</Label>
                    <ColorPicker
                      color={selectedElement.styles?.activeColor || '#3b82f6'}
                      onChange={(color) => updateStyle('activeColor', color)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Inactive Color</Label>
                    <ColorPicker
                      color={selectedElement.styles?.inactiveColor || '#d1d5db'}
                      onChange={(color) => updateStyle('inactiveColor', color)}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label className="text-xs">Gap</Label>
                    <span className="text-xs text-muted-foreground">{parseInt(selectedElement.styles?.gap) || 8}px</span>
                  </div>
                  <Slider
                    value={[parseInt(selectedElement.styles?.gap) || 8]}
                    onValueChange={(value) => updateStyle('gap', `${value[0]}px`)}
                    min={0}
                    max={20}
                    step={1}
                  />
                </div>
              </div>
            </>
          )}

          {/* Progress Bar Properties */}
          {selectedElement.type === 'progress-bar' && (
            <>
              <Separator />
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label className="text-xs">Progress (%)</Label>
                    <span className="text-xs text-muted-foreground">{selectedElement.progress || 0}%</span>
                  </div>
                  <Slider
                    value={[selectedElement.progress || 0]}
                    onValueChange={(value) => updateProperty('progress', value[0])}
                    min={0}
                    max={100}
                    step={1}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Background Color</Label>
                  <ColorPicker
                    color={selectedElement.styles?.backgroundColor || '#f3f4f6'}
                    onChange={(color) => updateStyle('backgroundColor', color)}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Fill Color</Label>
                  <ColorPicker
                    color={selectedElement.styles?.fillColor || '#3b82f6'}
                    onChange={(color) => updateStyle('fillColor', color)}
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label className="text-xs">Border Radius</Label>
                    <span className="text-xs text-muted-foreground">{parseInt(selectedElement.styles?.borderRadius) || 4}px</span>
                  </div>
                  <Slider
                    value={[parseInt(selectedElement.styles?.borderRadius) || 4]}
                    onValueChange={(value) => updateStyle('borderRadius', `${value[0]}px`)}
                    min={0}
                    max={50}
                    step={1}
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label className="text-xs">Height</Label>
                    <span className="text-xs text-muted-foreground">{selectedElement.height || 20}px</span>
                  </div>
                  <Slider
                    value={[selectedElement.height || 20]}
                    onValueChange={(value) => updateProperty('height', value[0])}
                    min={4}
                    max={100}
                    step={1}
                  />
                </div>
                <div className="flex items-center space-x-2 mt-2">
                  <input
                    type="checkbox"
                    id="striped"
                    checked={selectedElement.styles?.striped || false}
                    onChange={(e) => updateStyle('striped', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <Label htmlFor="striped" className="text-xs">Striped</Label>
                </div>
                <div className="flex items-center space-x-2 mt-2">
                  <input
                    type="checkbox"
                    id="animated"
                    checked={selectedElement.styles?.animated || false}
                    onChange={(e) => updateStyle('animated', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <Label htmlFor="animated" className="text-xs">Animated</Label>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PropertiesPanel;
